<script setup>
import { computed, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import AssistantSwitcher from 'dashboard/components-next/arrowsAi/assistant/AssistantSwitcher.vue';
import CreateAssistantDialog from 'dashboard/components-next/arrowsAi/assistant/CreateAssistantDialog.vue';
import HelpDialog from 'dashboard/components-next/arrowsAi/HelpDialog.vue';

defineProps({
  headerTitle: { type: String, default: '' },
  buttonLabel: { type: String, default: '' },
  isEmpty: { type: Boolean, default: false },
  showPaginationFooter: { type: Boolean, default: false },
  currentPage: { type: Number, default: 1 },
  totalCount: { type: Number, default: 0 },
  itemsPerPage: { type: Number, default: 25 },
});

const emit = defineEmits(['click', 'update:currentPage']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { getAssistant, canCreateAssistant, createLimit } =
  useArrowsAiAssistants();

const showSwitcher = ref(false);
const createDialog = ref(null);

const currentAssistant = computed(() => getAssistant(route.params.assistantId));

const switcherLabel = computed(
  () => currentAssistant.value?.name || t('ARROWS_AI.SWITCHER.NEW')
);

const openCreate = () => {
  if (!canCreateAssistant.value) {
    useAlert(t('ARROWS_AI.SWITCHER.LIMIT', { count: createLimit.value }));
    return;
  }
  showSwitcher.value = false;
  createDialog.value?.dialogRef.open();
};

const handleCreated = assistant => {
  router.push({
    name: 'arrows_ai_assistants_instructions',
    params: {
      accountId: route.params.accountId,
      assistantId: assistant.id,
    },
  });
};

defineExpose({ openCreate });
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div
          class="flex flex-col items-start justify-between w-full gap-4 py-6 lg:flex-row lg:items-center lg:h-20 lg:py-0 lg:gap-2"
        >
          <div class="flex items-center min-w-0 gap-3">
            <span class="text-xl font-medium truncate text-n-slate-12">
              {{ switcherLabel }}
            </span>
            <OnClickOutside @trigger="showSwitcher = false">
              <div class="relative">
                <Button
                  icon="i-lucide-chevron-down"
                  :variant="showSwitcher ? 'faded' : 'ghost'"
                  color="slate"
                  size="xs"
                  class="rounded-md hover:bg-n-slate-3 [&>span]:size-4"
                  @click="showSwitcher = !showSwitcher"
                />
                <AssistantSwitcher
                  v-if="showSwitcher"
                  class="absolute ltr:left-0 rtl:right-0 top-9"
                  @close="showSwitcher = false"
                  @create-assistant="openCreate"
                />
              </div>
            </OnClickOutside>
            <div v-if="headerTitle" class="w-0.5 h-4 rounded-2xl bg-n-weak" />
            <span
              v-if="headerTitle"
              class="text-xl font-medium text-n-slate-12"
            >
              {{ headerTitle }}
            </span>
          </div>
          <div class="flex items-center gap-4">
            <HelpDialog />
            <slot name="headerActions" />
            <Button
              v-if="buttonLabel"
              :label="buttonLabel"
              icon="i-lucide-plus"
              size="sm"
              @click="emit('click')"
            />
          </div>
        </div>
      </div>
    </header>
    <main class="flex-1 min-h-0 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl py-4 mx-auto">
        <slot v-if="isEmpty" name="emptyState" />
        <slot v-else />
      </div>
    </main>
    <footer v-if="showPaginationFooter" class="sticky bottom-0 z-10">
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="itemsPerPage"
        class="max-w-[67rem]"
        @update:current-page="emit('update:currentPage', $event)"
      />
    </footer>
    <CreateAssistantDialog ref="createDialog" @created="handleCreated" />
  </section>
</template>
